import React, { useState } from 'react';
import { Plus, Filter, FileText } from 'lucide-react';
import WorkCard from './WorkCard';
import { OFFICES, WORK_TYPES, isOnHold, isInBankFeeSubTab, isInCompletedSubTab, isArchived } from './helpers';

const SUBTABS = [
  { id: 'active', label: 'Active' },
  { id: 'onhold', label: 'On Hold' },
  { id: 'reportdone', label: 'Completed' },
  { id: 'bankfee', label: 'Bank Fee' },
];

export default function WorksTab({ reports, staff, staffById, onNewWork, ...handlers }) {
  const [office, setOffice] = useState('Karaikudi');
  const [sub, setSub] = useState('active');
  const [fType, setFType] = useState('All');
  const [fInsp, setFInsp] = useState('All');
  const [fPay, setFPay] = useState('All');

  const passes = (r) => {
    if (r.office !== office) return false;
    if (fType !== 'All' && (r.work_type || 'Valuation Report') !== fType) return false;
    if (fInsp === 'Completed' && !r.inspection_done) return false;
    if (fInsp === 'Not Completed' && r.inspection_done) return false;
    if (fPay !== 'All' && r.pay_mode !== fPay) return false;
    return true;
  };
  const base = reports.filter((r) => passes(r) && !isArchived(r));
  const onHoldCount = base.filter(isOnHold).length;
  const bankFeeCount = base.filter(isInBankFeeSubTab).length;
  const completedCount = base.filter(isInCompletedSubTab).length;
  const activeCount = base.length - onHoldCount - bankFeeCount - completedCount;
  const counts = { active: activeCount, onhold: onHoldCount, reportdone: completedCount, bankfee: bankFeeCount };

  const visible = base.filter((r) => {
    if (sub === 'active') return !isOnHold(r) && !isInBankFeeSubTab(r) && !isInCompletedSubTab(r);
    if (sub === 'onhold') return isOnHold(r);
    if (sub === 'bankfee') return isInBankFeeSubTab(r);
    if (sub === 'reportdone') return isInCompletedSubTab(r);
    return true;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const emptyCopy = {
    active: { h: 'No works yet', p: 'Tap "+ New Work" to create your first valuation work.' },
    onhold: { h: 'No works on hold', p: 'Works move here when marked "On Hold" with a reason.' },
    reportdone: { h: 'No completed reports yet', p: 'Mark "Report: Completed" to move works here. They stay until fully collected and moved to Closed.' },
    bankfee: { h: 'No bank fees pending', p: 'Works appear here when Report is completed and Pay Mode is "Bank Credit", after submission to the bank.' },
  }[sub];

  return (
    <div data-testid="works-tab">
      <div className="fin-office-tabs">
        {OFFICES.map((o) => <button key={o} className={`fin-office-tab${office === o ? ' active' : ''}`} onClick={() => setOffice(o)} data-testid={`works-office-${o}`}>{o}</button>)}
      </div>

      <div className="status-tabs">
        {SUBTABS.map((s) => <button key={s.id} className={`status-tab${sub === s.id ? ' active' : ''}`} onClick={() => setSub(s.id)} data-testid={`subtab-${s.id}`}>{s.label}<span className="count">({counts[s.id]})</span></button>)}
      </div>

      <div className="filter-row">
        <div className="filters">
          <span style={{ color: '#78716c', display: 'inline-flex' }}><Filter style={{ width: 14, height: 14 }} /></span>
          <select className="filter-select" value={fType} onChange={(e) => setFType(e.target.value)} data-testid="filter-type"><option>All</option>{WORK_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <select className="filter-select" value={fInsp} onChange={(e) => setFInsp(e.target.value)} data-testid="filter-insp"><option>All</option><option>Completed</option><option>Not Completed</option></select>
          <select className="filter-select" value={fPay} onChange={(e) => setFPay(e.target.value)} data-testid="filter-pay"><option>All</option><option>Pending</option><option>Cash</option><option>UPI</option><option>Bank Credit</option></select>
        </div>
        <button className="btn-primary" onClick={onNewWork} data-testid="new-work-btn"><Plus style={{ width: 13, height: 13 }} /> New Work</button>
      </div>

      {visible.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><FileText style={{ width: 44, height: 44, strokeWidth: 1.5 }} /></div>
          <h3>{emptyCopy.h}</h3><p>{emptyCopy.p}</p>
          {sub !== 'onhold' && sub !== 'bankfee' && <div className="empty-actions"><button className="btn-primary" onClick={onNewWork}><Plus style={{ width: 13, height: 13 }} /> Create First Work</button></div>}
        </div>
      ) : (
        <div className="reports">
          {visible.map((r) => <WorkCard key={r.id} work={r} staffById={staffById} {...handlers} />)}
        </div>
      )}
    </div>
  );
}
